import { Clonable, User } from '../../..';

export class Gallery extends Clonable {
  constructor(
    public readonly id: string,
    public readonly slug: string,
    public readonly title: string,
    public readonly description: string | undefined,
    public readonly teamId: string,
    public readonly createdBy: string,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
    public readonly itemIds: readonly string[],
    public readonly coverItemId: string | undefined,
    public readonly publicPasswordHash: string | undefined,
    public readonly publicPassword: string | undefined,
    public readonly contributorUserIds: readonly string[],
    public readonly isOpenToTeam: boolean,
    public readonly expiresAt: Date | undefined,
  ) {
    super();
  }

  /** True if `expiresAt` is set and has passed. Authoritative time should be the server's. */
  public isExpired(now: Date = new Date()): boolean {
    return !!this.expiresAt && this.expiresAt.getTime() <= now.getTime();
  }

  /**
   * Whole-day count until expiration (negative if already expired). Returns undefined when
   * the gallery has no expiration set.
   */
  public daysUntilExpiration(now: Date = new Date()): number | undefined {
    if (!this.expiresAt) return undefined;
    const ms = this.expiresAt.getTime() - now.getTime();
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  }

  public hasPublicPassword(): boolean {
    return !!this.publicPasswordHash;
  }

  public get effectiveCoverItemId(): string | undefined {
    return this.coverItemId ?? this.itemIds[0];
  }

  /**
   * True when the given user manages galleries team-wide (Admin or Feed Manager). Used by both
   * the visibility filter on the galleries list and the permission checks for delete.
   */
  public static isTeamGalleryManager(user: User): boolean {
    return user.isAdmin || user.isFeedManager;
  }

  /** Owner is implicit via `createdBy`; manager roles always have access. */
  public canBeViewedBy(user: User): boolean {
    return this.canContributeBy(user) || Gallery.isTeamGalleryManager(user);
  }

  /**
   * Contributors can add items, remove items, edit metadata, and edit the contributor list.
   * The only thing they can't do is delete the gallery.
   */
  public canContributeBy(user: User): boolean {
    if (this.isOpenToTeam && user.teamId === this.teamId) return true;
    if (user.id === this.createdBy) return true;
    return this.contributorUserIds.includes(user.id);
  }

  /**
   * Elevated permission tier. Owners and team gallery managers (admin or feed-manager)
   * can delete the gallery, change the password, and edit who else contributes. Plain
   * contributors can add/remove items and edit the title/description but can't touch
   * these sensitive controls.
   */
  public canManageBy(user: User): boolean {
    return user.id === this.createdBy || Gallery.isTeamGalleryManager(user);
  }
}
